/**
 * VEYR homepage interactions.
 * Progressive, reduced-motion safe and compatible with Shopify section reloads.
 */
(function () {
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function qsa(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function qs(root, selector) {
    return root.querySelector(selector);
  }

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function visibleFocusables(container) {
    return qsa(container, FOCUSABLE).filter(function (node) {
      return !!(node.offsetWidth || node.offsetHeight || node.getClientRects().length);
    });
  }

  function initReveal(root) {
    var nodes = qsa(root, '[data-veyr-reveal]');
    if (!nodes.length) return function () {};

    root.classList.add('veyr-motion-ready');

    if (reducedMotion() || !('IntersectionObserver' in window)) {
      nodes.forEach(function (node) { node.classList.add('is-visible'); });
      return function () {};
    }

    nodes.forEach(function (node) {
      if (node.getBoundingClientRect().top < window.innerHeight * 0.98) {
        node.classList.add('is-visible');
      }
    });

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -4% 0px' });

    nodes.forEach(function (node) {
      if (!node.classList.contains('is-visible')) observer.observe(node);
    });

    return function () { observer.disconnect(); };
  }

  function initScroll(root) {
    var nav = qs(root, '[data-veyr-home-nav]');
    var hero = qs(root, '[data-veyr-parallax="hero"]');
    var human = qs(root, '[data-veyr-parallax="human"]');
    var ticking = false;

    function progress(node) {
      if (!node) return 0;
      var rect = node.getBoundingClientRect();
      return Math.max(-1, Math.min(1, (window.innerHeight - rect.top) / (window.innerHeight + rect.height)));
    }

    function update() {
      ticking = false;
      if (nav) nav.classList.toggle('is-scrolled', window.scrollY > 24);

      if (reducedMotion() || window.innerWidth < 900) return;
      if (hero) hero.style.setProperty('--veyr-parallax-y', String(Math.min(window.scrollY * 0.035, 24)) + 'px');
      if (human) human.style.setProperty('--veyr-parallax-y', String((progress(human) - 0.5) * 24) + 'px');
    }

    function requestUpdate() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    update();

    return function () {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
  }

  function initMenu(root) {
    var openButton = qs(root, '[data-veyr-menu-open]');
    var closeButton = qs(root, '[data-veyr-menu-close]');
    var menu = qs(root, '[data-veyr-menu]');
    if (!openButton || !closeButton || !menu) return function () {};

    var previousFocus = null;
    var timer = 0;
    var open = false;

    function onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
        return;
      }

      if (event.key !== 'Tab') return;
      var nodes = visibleFocusables(menu);
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function openMenu() {
      if (open) return;
      open = true;
      window.clearTimeout(timer);
      previousFocus = document.activeElement;
      menu.hidden = false;
      document.body.classList.add('veyr-home-menu-open');
      openButton.setAttribute('aria-expanded', 'true');
      menu.addEventListener('keydown', onKeydown);

      window.requestAnimationFrame(function () {
        menu.classList.add('is-open');
        closeButton.focus();
      });
    }

    function finishClose() {
      menu.hidden = true;
      if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
    }

    function closeMenu() {
      if (!open) return;
      open = false;
      menu.classList.remove('is-open');
      document.body.classList.remove('veyr-home-menu-open');
      openButton.setAttribute('aria-expanded', 'false');
      menu.removeEventListener('keydown', onKeydown);

      if (reducedMotion()) {
        finishClose();
      } else {
        timer = window.setTimeout(finishClose, 460);
      }
    }

    openButton.addEventListener('click', openMenu);
    closeButton.addEventListener('click', closeMenu);
    qsa(menu, 'a').forEach(function (link) { link.addEventListener('click', closeMenu); });

    return function () {
      window.clearTimeout(timer);
      openButton.removeEventListener('click', openMenu);
      closeButton.removeEventListener('click', closeMenu);
      qsa(menu, 'a').forEach(function (link) { link.removeEventListener('click', closeMenu); });
      menu.removeEventListener('keydown', onKeydown);
      document.body.classList.remove('veyr-home-menu-open');
    };
  }

  function initSound(root) {
    var button = qs(root, '[data-veyr-sound]');
    var label = qs(root, '[data-veyr-sound-label]');
    var AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!button || !label || !AudioContextClass) {
      if (button) button.hidden = true;
      return function () {};
    }

    var context = null;
    var activeNodes = [];
    var finishTimer = 0;
    var playing = false;
    var duration = 12;

    function track(node) {
      activeNodes.push(node);
      return node;
    }

    function noiseBuffer(audioContext, seconds, brown) {
      var length = Math.floor(audioContext.sampleRate * seconds);
      var buffer = audioContext.createBuffer(1, length, audioContext.sampleRate);
      var data = buffer.getChannelData(0);
      var last = 0;

      for (var index = 0; index < length; index += 1) {
        var white = Math.random() * 2 - 1;
        if (brown) {
          last = (last + 0.02 * white) / 1.02;
          data[index] = last * 3.1;
        } else {
          data[index] = white * 0.6;
        }
      }
      return buffer;
    }

    function setState(state) {
      playing = state;
      button.setAttribute('aria-pressed', state ? 'true' : 'false');
      button.setAttribute('aria-label', state ? 'Stop the VEYR landscape sound' : 'Play the VEYR landscape sound');
      label.textContent = state ? 'Sound on' : 'Sound off';
    }

    function stopSound() {
      window.clearTimeout(finishTimer);
      finishTimer = 0;
      activeNodes.forEach(function (node) {
        try {
          if (typeof node.stop === 'function') node.stop();
          if (typeof node.disconnect === 'function') node.disconnect();
        } catch (error) {
          // A source can already have ended.
        }
      });
      activeNodes = [];

      if (context) {
        var closing = context;
        context = null;
        closing.close().catch(function () {});
      }
      setState(false);
    }

    function startSound() {
      context = new AudioContextClass();
      var now = context.currentTime;
      var master = track(context.createGain());
      var lowSource = track(context.createBufferSource());
      var airSource = track(context.createBufferSource());
      var lowFilter = track(context.createBiquadFilter());
      var airHigh = track(context.createBiquadFilter());
      var airLow = track(context.createBiquadFilter());
      var lowGain = track(context.createGain());
      var airGain = track(context.createGain());
      var lfo = track(context.createOscillator());
      var lfoDepth = track(context.createGain());

      lowSource.buffer = noiseBuffer(context, 2.3, true);
      lowSource.loop = true;
      airSource.buffer = noiseBuffer(context, 1.7, false);
      airSource.loop = true;

      lowFilter.type = 'lowpass';
      lowFilter.frequency.value = 590;
      lowFilter.Q.value = 0.65;
      airHigh.type = 'highpass';
      airHigh.frequency.value = 1100;
      airLow.type = 'lowpass';
      airLow.frequency.value = 3600;
      lowGain.gain.value = 0.68;
      airGain.gain.value = 0.085;

      lfo.type = 'sine';
      lfo.frequency.value = 0.09;
      lfoDepth.gain.value = 145;
      lfo.connect(lfoDepth);
      lfoDepth.connect(lowFilter.frequency);

      lowSource.connect(lowFilter);
      lowFilter.connect(lowGain);
      lowGain.connect(master);
      airSource.connect(airHigh);
      airHigh.connect(airLow);
      airLow.connect(airGain);
      airGain.connect(master);
      master.connect(context.destination);

      master.gain.setValueAtTime(0, now);
      master.gain.linearRampToValueAtTime(0.058, now + 1.4);
      master.gain.setValueAtTime(0.058, now + duration - 2.2);
      master.gain.linearRampToValueAtTime(0, now + duration);

      lowSource.start(now, Math.random());
      airSource.start(now, Math.random());
      lfo.start(now);
      setState(true);
      finishTimer = window.setTimeout(stopSound, duration * 1000 + 120);
      context.resume().catch(stopSound);
    }

    function toggleSound() {
      if (playing) stopSound();
      else startSound();
    }

    button.addEventListener('click', toggleSound);
    window.addEventListener('pagehide', stopSound);

    return function () {
      button.removeEventListener('click', toggleSound);
      window.removeEventListener('pagehide', stopSound);
      stopSound();
    };
  }

  function initCartCount(root) {
    var count = qs(root, '[data-veyr-cart-count]');
    if (!count) return function () {};

    function update(event) {
      var detail = event && event.detail;
      var resource = detail && detail.resource;
      var value = resource && resource.item_count;
      if (typeof value === 'undefined' && detail) value = detail.item_count;
      if (typeof value === 'undefined') return;
      count.textContent = String(value);
      var link = count.closest('a');
      if (link) link.setAttribute('aria-label', 'Cart, ' + value + (value === 1 ? ' item' : ' items'));
    }

    document.addEventListener('cart:update', update);
    return function () { document.removeEventListener('cart:update', update); };
  }

  function boot(root) {
    if (!root || root.getAttribute('data-veyr-ready') === 'true') return;
    root.setAttribute('data-veyr-ready', 'true');
    var cleanups = [initReveal(root), initScroll(root), initMenu(root), initSound(root), initCartCount(root)];
    root.__veyrCleanup = function () {
      cleanups.forEach(function (cleanup) { if (typeof cleanup === 'function') cleanup(); });
    };
  }

  function bootAll() {
    qsa(document, '[data-veyr-home]').forEach(boot);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootAll, { once: true });
  else bootAll();

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target && event.target.querySelector ? event.target.querySelector('[data-veyr-home]') : null;
    if (root && typeof root.__veyrCleanup === 'function') root.__veyrCleanup();
  });

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target && event.target.querySelector ? event.target.querySelector('[data-veyr-home]') : null;
    if (root) {
      root.removeAttribute('data-veyr-ready');
      boot(root);
    }
  });
})();
